import { useParams } from 'react-router-dom';
import {
  useFreshdeskEmailIntegrationAutoSetupMutation,
  useGetProjectByIdQuery,
  useUpdateProjectMutation,
} from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import moment from 'moment/moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { EditFreshdeskEmailAutoSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshdeskEmailIntegrationSettings/EditFreshdeskEmailAutoSetupSettings/EditFreshdeskEmailAutoSetupSettings';
import { EditFreshdeskEmailAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshdeskEmailIntegrationSettings/EditFreshdeskEmailAdvancedSettings/EditFreshdeskEmailAdvancedSettings';
import { EditFreshdeskEmailApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshdeskEmailIntegrationSettings/EditFreshdeskEmailApiDetailsSettings/EditFreshdeskEmailApiDetailsSettings';

const AccordionIcon = ({ open }) => (
  <svg
    className={clsx('transition-transform', open && 'rotate-180')}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M12.6523 0.651864L6.35244 6.65186L0.652531 0.65187"
      stroke="#7A6BE5"
      strokeWidth="1.79997"
    />
  </svg>
);

export const EditFreshdeskEmailIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const loader = useLoader();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const isSetupDone = integration?.integration_data?.is_setup;
  const duration = currentProject?.autoclose_time_window_minutes
    ? moment.duration(currentProject?.autoclose_time_window_minutes, 'minutes')
    : 0;
  const autocloseHours = duration ? Math.floor(duration.asHours()) : 0;
  const autocloseMinutes = duration ? duration.minutes() : 0;
  const [updateProject, { isLoading: isLoadingUpdateProject }] = useUpdateProjectMutation();
  const [setFreshdeskAutoSetup, { isLoading: isLoadingSetZendeskChatAutoSetup }] =
    useFreshdeskEmailIntegrationAutoSetupMutation();

  useEffect(() => {
    if (isLoadingUpdateProject || isLoadingSetZendeskChatAutoSetup) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateProject, isLoadingSetZendeskChatAutoSetup]);

  const methods = useForm<any>({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain,
      name: integration?.integration_data?.name,
      include_subject_first_response: integration?.integration_data?.include_subject_first_response,
      reply_to_note: integration?.integration_data?.reply_to_note,
      api_key: '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
      include_custom_fields: integration?.integration_data?.include_custom_fields || false,
      custom_fields: integration?.integration_data?.custom_fields || null,
    },
    resolver: yupResolver(
      object({
        subdomain: stringScheme({ required: false }).label('Zendesk subdomain'),
        api_key: stringScheme({ required: false }).label('API token'),
        email: stringScheme({ required: false }).label('Admin email address'),
      }),
    ),
  });

  const submit = async (data) => {
    if (data.responder_id) {
      await setFreshdeskAutoSetup({
        projectId: id,
        integrationId: integration.id,
        body: {
          responder_id: +data.responder_id,
          responder_email: data.responder_email,
          escalate_assign_group_id: data.escalate_assign_group_id
            ? +data.escalate_assign_group_id
            : 0,
        },
      });
    }

    delete data.responder_id;
    delete data.responder_email;
    delete data.escalate_assign_group_id;

    const autocloseMinutes = moment
      .duration(data.autocloseHours, 'hours')
      .add(data.autocloseMinutes, 'minutes')
      .asMinutes();

    const isAutocloseChanged = currentProject?.autoclose_time_window_minutes !== autocloseMinutes;

    if (isAutocloseChanged) {
      await updateProject({
        projectId: currentProject.id,
        name: currentProject?.name,
        rate_limit_project: currentProject?.rate_limit_project,
        rate_limit_project_per: currentProject?.rate_limit_project_per,
        rate_limit_chat: currentProject?.rate_limit_chat,
        rate_limit_chat_per: currentProject?.rate_limit_chat_per,
        rate_limit_max_chats: currentProject?.rate_limit_max_chats,
        rate_limit_max_chats_per: currentProject?.rate_limit_max_chats_per,
        cocbi_project_id: currentProject?.cocbi_project_id,
        use_cocbi_model: currentProject?.use_cocbi_model,
        ai_reply_generation: currentProject?.ai_reply_generation,
        ai_text_lense: currentProject?.ai_text_lense,
        autoclose_time_window_minutes: autocloseMinutes ? autocloseMinutes : null,
      });
    }
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (!data.api_key) {
      delete data.api_key;
    }

    if (data.include_custom_fields) {
      data.custom_fields = data.custom_fields.map((field) => {
        return { id: field.id, name: `${field.name}` };
      });
    } else {
      data.custom_fields = null;
    }

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
        subdomain: getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain,
        name: currentProject.name,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          {!isSetupDone && (
            <AccordionItem
              value="auto-setup-settings"
              className={clsx('group bg-white border-b-[#A498FF4D]')}
            >
              <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
                <div className="flex row items-center justify-between w-full">
                  <div>
                    <p className={'text-[#7A6BE5] font-semibold text-sm'}>Auto Setup Settings</p>
                  </div>
                  <div>
                    <AccordionIcon open={accordionValue.includes('ticket-status-settings')} />
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-5">
                <EditFreshdeskEmailAutoSetupSettings integration={integration} />
              </AccordionContent>
            </AccordionItem>
          )}
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshdeskEmailAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshdeskEmailApiDetailsSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
